import apiClient from "@shared/config/axiosConfig";

/**
 * Servicio para gestión de reportes de mantenimiento
 * Los reportes incluyen el campo idfrecuenciamantenimiento que permite filtrar por:
 * - Semanal, Mensual, Trimestral, Semestral, Anual, No Rutinario
 */
const reportesService = {
  /**
   * Obtener todos los reportes
   * @returns {Promise<Array>} Lista de reportes con idfrecuenciamantenimiento
   */
  getAllReportes: async () => {
    try {
      const response = await apiClient.get("/reportes");
      return response.data;
    } catch (error) {
      console.error("Error obteniendo reportes:", error);
      throw error;
    }
  },

  /**
   * Obtener reporte por ID
   * @param {number} id - ID del reporte
   * @returns {Promise<Object>} Reporte
   */
  getReporteById: async (id) => {
    try {
      const response = await apiClient.get(`/reportes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Obtener reportes por planta
   * @param {number} idPlanta - ID de la planta
   * @returns {Promise<Array>} Reportes de la planta
   */
  getReportesByPlanta: async (idPlanta) => {
    try {
      const response = await apiClient.get(`/reportes/planta/${idPlanta}`);
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo reportes de planta ${idPlanta}:`, error);
      throw error;
    }
  },

  /**
   * Obtener reportes por usuario
   * @param {number} idUsuario - ID del usuario
   * @returns {Promise<Array>} Reportes del usuario
   */
  getReportesByUsuario: async (idUsuario) => {
    try {
      const response = await apiClient.get(`/reportes/usuario/${idUsuario}`);
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo reportes de usuario ${idUsuario}:`,
        error,
      );
      throw error;
    }
  },

  /**
   * Crear nuevo reporte
   * @param {Object} reporteData - Datos del reporte
   * @returns {Promise<Object>} Reporte creado
   */
  createReporte: async (reporteData) => {
    try {
      const response = await apiClient.post("/reportes", reporteData);
      return response.data;
    } catch (error) {
      console.error("Error creando reporte:", error);
      throw error;
    }
  },

  /**
   * Actualizar reporte
   * @param {number} id - ID del reporte
   * @param {Object} reporteData - Datos actualizados
   * @returns {Promise<Object>} Reporte actualizado
   */
  updateReporte: async (id, reporteData) => {
    try {
      const response = await apiClient.put(`/reportes/${id}`, reporteData);
      return response.data;
    } catch (error) {
      console.error(`Error actualizando reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Eliminar reporte
   * @param {number} id - ID del reporte
   * @returns {Promise<Object>} Confirmación
   */
  deleteReporte: async (id) => {
    try {
      const response = await apiClient.delete(`/reportes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error eliminando reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Firmar reporte
   * @param {number} id - ID del reporte
   * @param {Object} firmaData - Datos de la firma
   * @returns {Promise<Object>} Reporte firmado
   */
  firmarReporte: async (id, firmaData) => {
    try {
      const response = await apiClient.post(
        `/reportes/${id}/firmar`,
        firmaData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error firmando reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de reportes
   * @param {Object} filtros - Filtros para estadísticas
   * @returns {Promise<Object>} Estadísticas
   */
  getEstadisticas: async (filtros = {}) => {
    try {
      const response = await apiClient.post("/reportes/estadisticas", filtros);
      return response.data;
    } catch (error) {
      console.error("Error obteniendo estadísticas:", error);
      throw error;
    }
  },

  /**
   * Generar PDF del reporte
   * @param {number} id - ID del reporte
   * @returns {Promise<Blob>} PDF generado
   */
  generarPDF: async (id) => {
    try {
      const response = await apiClient.get(`/reportes/${id}/pdf`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error(`Error generando PDF del reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Agregar escaneado (PDF) a un reporte digital existente (modo híbrido)
   * @param {number} id - ID del reporte digital
   * @param {string} scanPath - Ruta del archivo PDF en S3
   * @returns {Promise<Object>} Reporte actualizado
   */
  agregarEscaneado: async (id, scanPath) => {
    try {
      const response = await apiClient.patch(
        `/reportes/${id}/agregar-escaneado`,
        { scanPath },
      );
      return response.data;
    } catch (error) {
      console.error(`Error agregando escaneado al reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Agregar datos digitales a un reporte escaneado existente (modo híbrido)
   * @param {number} id - ID del reporte escaneado
   * @param {Object} respuesta - Datos del reporte digital
   * @returns {Promise<Object>} Reporte actualizado
   */
  agregarDigital: async (id, respuesta) => {
    try {
      const response = await apiClient.patch(
        `/reportes/${id}/agregar-digital`,
        { respuesta },
      );
      return response.data;
    } catch (error) {
      console.error(`Error agregando digital al reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Desvincular escaneado de un reporte híbrido para dejarlo solo digital
   * @param {number} id - ID del reporte híbrido
   * @returns {Promise<Object>} Reporte actualizado
   */
  desvincularEscaneado: async (id) => {
    try {
      const response = await apiClient.patch(
        `/reportes/${id}/desvincular-escaneado`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error desvinculando escaneado del reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Obtener la modalidad de un reporte (digital, escaneado, híbrido)
   * @param {number} id - ID del reporte
   * @returns {Promise<Object>} { modalidad, tieneDigital, tieneEscaneado }
   */
  obtenerModalidad: async (id) => {
    try {
      const response = await apiClient.get(`/reportes/${id}/modalidad`);
      return response.data;
    } catch (error) {
      console.error(`Error obteniendo modalidad del reporte ${id}:`, error);
      throw error;
    }
  },

  /**
   * Obtener historial de auditoría de un reporte
   * @param {number} id - ID del reporte
   * @returns {Promise<Object>} { idreporte, historial, ultimaAccion }
   */
  obtenerHistorialAuditoria: async (id) => {
    try {
      const response = await apiClient.get(
        `/reportes/${id}/historial-auditoria`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error obteniendo historial de auditoría del reporte ${id}:`,
        error,
      );
      throw error;
    }
  },
};

export default reportesService;
